'use client';

/**
 * **The share drawers' market-price seam** (VALE-1008), replacing the
 * `SharePriceKept` adapter ([INVDRAW-12] / VALE-83, [INVDRAW-18] / VALE-131).
 *
 * The price figures are ordinary attribute rows now (`marketFigure`), so the
 * thing that used to be a kept component wrapping `SharePriceBlock` is no longer
 * a component at all: it is the *data* those rows are built from, and a host
 * hook is where data a spec cannot fetch has always come from. Everything that
 * made the adapter subtle survives here, in the same shape.
 *
 * ## What is gone, and why it was only ever scaffolding
 *
 * The adapter's local `react-hook-form` instance existed for one reason: to let
 * `SharePriceBlock` keep running **unmodified** inside a drawer that has no RHF
 * form of its own. It was a value store standing in for a form, watched so a
 * debounced diff could be forwarded to the drawer's real commit path. With the
 * figures on rows, the grid owns the draft and `onCommit` is reached directly, so
 * the store, the watch and the 600ms manual-edit debounce all go with it. Nothing
 * is now debounced because nothing now writes per keystroke: a row commits once,
 * on close.
 *
 * ## What survives unchanged, because it is domain truth rather than plumbing
 *
 * · **{@link priceSource} and {@link firstHolding}** — where a type keeps its
 *   figures, including the legacy-flat `hin-shares` display fallback.
 * · **{@link buildPricePatch}** — the whole-`portfolio`-array rebuild, and the
 *   item-level `dodPriceSourcedAt` mirror that keeps lockstep with the
 *   holding-level key.
 * · **The fetch-and-apply effect** — ported from `SharesForm` originally, and
 *   still only ever overwriting a zero/missing figure or a genuine unit-count
 *   change, so a manual override survives (LF-4722).
 */

import { patchKeyPaths } from '@/components/ai-review/change-status';
import { useEffect, useMemo, useRef } from 'react';
import { useStockPrice } from '@/lib/services/stock-prices';
import type { StockPriceResponse } from '@/lib/services/stock-prices/types';
import type {
  InventoryItem,
  UpdateInventoryItemRequest
} from '@/lib/services/inventory-items/types';

/**
 * **Where a type keeps its price figures.**
 *
 * · `item-info` — top-level `itemInfo` keys. `srn-shares`.
 * · `portfolio-holding` — the same keys inside `itemInfo.portfolio[0]`.
 *   `hin-shares`, whose form has only ever bound to `portfolio.0` — there is no
 *   repeating list, and the backend's `explode-hin-portfolio.ts` exists precisely
 *   to split a multi-stock extraction into one item per holding.
 */
export type SharePriceScope = 'item-info' | 'portfolio-holding';

/**
 * **Which types carry market-priced share figures, and where they keep them** —
 * stated once here rather than at each host (VALE-1008).
 *
 * Two hosts render these builders (the item drawer and the add dialog), so a
 * slug list spelled out at both is the restatement that drifts the moment a third
 * share type appears. `srn-shares` keeps its figures as top-level `itemInfo`
 * keys; `hin-shares` keeps the same keys inside `itemInfo.portfolio[0]`.
 */
const SHARE_PRICE_SCOPES: Record<string, SharePriceScope> = {
  'srn-shares': 'item-info',
  'hin-shares': 'portfolio-holding'
};

/** Whether this type carries share price figures at all. */
export function isSharePriceType(typeSlug: string): boolean {
  return typeSlug in SHARE_PRICE_SCOPES;
}

/**
 * Where this type keeps its figures. Defaults to `item-info` for a non-share
 * type, which is inert: the hook is passed `enabled: false` for those and returns
 * `null` before the scope is ever read.
 */
export function shareScopeFor(typeSlug: string): SharePriceScope {
  return SHARE_PRICE_SCOPES[typeSlug] ?? 'item-info';
}

/** Written by the price rows or the fetch-apply effect. */
export const PRICE_KEYS = [
  'currentPrice',
  'currentValue',
  'currentPriceOverridden',
  'sharePriceAtDateOfDeath',
  'valueAtDateOfDeath',
  'dodPriceSourcedAt'
] as const;

type Holding = Record<string, unknown>;

/** The keys a holding owns — the six price figures plus the holding's identity. */
const HOLDING_KEYS = [
  ...PRICE_KEYS,
  'ticker',
  'companyName',
  'numberOfShares'
] as const;

/** Holding 0, or the holding a legacy flat-shaped record implies. */
function firstHolding(itemInfo: Record<string, unknown>): Holding {
  const holdings = Array.isArray(itemInfo.portfolio) ? itemInfo.portfolio : [];
  const stored = holdings[0] as Holding | undefined;
  if (stored) return stored;

  const legacy: Holding = {};
  for (const key of HOLDING_KEYS) {
    if (itemInfo[key] !== undefined) legacy[key] = itemInfo[key];
  }
  return legacy;
}

/**
 * The record the price figures actually live on.
 *
 * For `portfolio-holding` the top-level keys are read **underneath** the holding:
 * a legacy flat-shaped `hin-shares` record (ticker / numberOfShares / the price
 * fields at the top level, no `portfolio` array — the shape every
 * pre-portfolio-array row still carries) would otherwise render empty price rows.
 * This is a display fallback only, and exactly the one `HINPortfolioForm`'s own
 * read mode already applies (`holding.x || itemInfo.x`); it does **not** fix
 * VALE-123, which is about `normalizeHinPortfolioData` zero-seeding that same
 * shape on the flag-off form path.
 */
export function priceSource(
  itemInfo: Record<string, unknown>,
  scope: SharePriceScope
): Record<string, unknown> {
  if (scope === 'item-info') return itemInfo;
  return { ...itemInfo, ...firstHolding(itemInfo) };
}

/**
 * The outgoing patch for a set of changed price keys.
 *
 * `portfolio-holding` rebuilds the **whole** `portfolio` array from the cached
 * item with the changed keys folded into holding 0 — the backend merges `itemInfo`
 * one level deep only, so a per-key patch here would replace the array with a
 * single-key object and drop every sibling holding key.
 *
 * `dodPriceSourcedAt` is mirrored to the **item level** as well, to stay in
 * lockstep with the holding-level key — the same lockstep `HINPortfolioForm`
 * writes.
 */
export function buildPricePatch(
  changed: Record<string, unknown>,
  itemInfo: Record<string, unknown>,
  scope: SharePriceScope
): UpdateInventoryItemRequest {
  if (scope === 'item-info') return { itemInfo: changed };

  const holdings = Array.isArray(itemInfo.portfolio) ? itemInfo.portfolio : [];
  const next = [
    { ...firstHolding(itemInfo), ...changed },
    ...holdings.slice(1)
  ];

  return {
    itemInfo: {
      portfolio: next,
      ...('dodPriceSourcedAt' in changed
        ? { dodPriceSourcedAt: changed.dodPriceSourcedAt }
        : {})
    }
  };
}

/**
 * **Which diff keys a price patch writes, for a given scope** (VALE-1597).
 *
 * The two figure rows have to DECLARE their keys to the AI review, because
 * their `toPatch` compares the draft against the record and declines when
 * nothing moved — so asking it during render, where the draft is the record,
 * answers nothing. See `AttributeSpec.diffKey`.
 *
 * Derived from {@link buildPricePatch} rather than typed out at the call site,
 * because WHERE a share's figures live is a property of the scope: `item-info`
 * keeps them flat, `portfolio` folds them into a holdings array under one key.
 * A hand-written list would be right for one scope and name a field the other
 * never writes.
 *
 * **Deliberately not routed through `SharePriceSync.buildPatch`**, which is the
 * same function bound to an item: that one is the live commit path and a host
 * may legitimately instrument it, so calling it to ask a question put a
 * phantom entry in front of every real commit in its call log. The key paths do
 * not depend on the record's contents, so the pure function with an empty one
 * gives the same answer with nothing observing it.
 */
export function pricePatchKeyPaths(
  changed: Record<string, unknown>,
  scope: SharePriceScope
): string[] {
  return patchKeyPaths(
    buildPricePatch(changed, {}, scope) as unknown as Record<string, unknown>
  );
}

const toNum = (v: unknown): number | undefined =>
  typeof v === 'number' && !Number.isNaN(v) ? v : undefined;

const isZeroOrMissing = (v: unknown) =>
  v === undefined || v === null || v === 0;

/** The figures the two price rows read out, already narrowed off the record. */
export interface SharePriceFigures {
  ticker?: string;
  numberOfShares?: number;
  lastPriceUpdatedAt?: string;
  dodPrice?: number;
  dodValue?: number;
  currentPrice?: number;
  currentValue?: number;
  /** `itemInfo.currentPriceOverridden` — the reader has taken today's figure over. */
  overridden: boolean;
}

/**
 * Everything the share spec builders need that a pure spec cannot fetch. Threaded
 * onto `InventoryBlockContext` by whichever host is rendering, exactly as
 * `institutions` and `contacts` already are.
 */
export interface SharePriceSync {
  scope: SharePriceScope;
  figures: SharePriceFigures;
  /** The latest fetched figures, for restoring when manual entry is answered `No`. */
  market: StockPriceResponse | null;
  isFetchingPrice: boolean;
  /** Why no price is available, if so — rendered as a banner above the rows (VALE-763). */
  priceNotice: string | null;
  automaticPricingUnavailable: boolean;
  /** Wraps {@link buildPricePatch} against the item this sync is bound to. */
  buildPatch: (changed: Record<string, unknown>) => UpdateInventoryItemRequest;
}

export interface UseSharePriceSyncArgs {
  item: InventoryItem;
  scope: SharePriceScope;
  dateOfDeath?: string;
  /** Whether this item type carries share prices at all. */
  enabled: boolean;
  onCommit: (patch: UpdateInventoryItemRequest) => void;
}

export function useSharePriceSync({
  item,
  scope,
  dateOfDeath,
  enabled,
  onCommit
}: UseSharePriceSyncArgs): SharePriceSync | null {
  const itemInfo = useMemo(
    () => (item.itemInfo as Record<string, unknown> | null) ?? {},
    [item.itemInfo]
  );
  const source = useMemo(() => priceSource(itemInfo, scope), [itemInfo, scope]);

  const ticker =
    typeof source.ticker === 'string' && source.ticker
      ? source.ticker
      : undefined;
  const numberOfShares = toNum(source.numberOfShares);

  const {
    data: priceData,
    isLoading: isFetchingPrice,
    notice: priceNotice,
    automaticPricingUnavailable
  } = useStockPrice({
    ticker,
    dateOfDeath,
    numberOfShares,
    exchange: 'AU',
    enabled: enabled && !!ticker
  });

  /*
    **The fetch-apply effect, committing straight through.**

    The adapter wrote into a local RHF store and let a watcher forward the diff;
    the rows commit for themselves now, so this writes the one patch it means.

    Two guards keep it from looping on its own commit. The *conditions* are the
    original's and are already self-limiting — a figure is only auto-filled when
    it is zero or missing, so the write that fills it stops the next run. The
    `appliedRef` is belt-and-braces for the case those cannot cover: a commit
    that never settles back into the cache (an in-flight PATCH, a failed one)
    would otherwise re-fire this effect on every re-render for as long as the
    figure stayed empty. Keyed on the fetched payload plus the unit count, which
    together are the only inputs that can legitimately produce a *new* apply.
  */
  const lastPricedUnitsRef = useRef<number | undefined>(numberOfShares);
  const appliedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !priceData) return;

    const unitsChanged = numberOfShares !== lastPricedUnitsRef.current;
    const applyKey = `${priceData.ticker}|${priceData.currentPrice ?? ''}|${
      priceData.sharePriceAtDateOfDeath ?? ''
    }|${numberOfShares ?? ''}`;
    if (appliedRef.current === applyKey) return;

    const changed: Record<string, unknown> = {};

    const overridden = source.currentPriceOverridden === true;
    const applyCurrent =
      !overridden && (unitsChanged || isZeroOrMissing(source.currentValue));
    if (applyCurrent) {
      if (priceData.currentPrice !== undefined) {
        changed.currentPrice = priceData.currentPrice;
      }
      if (priceData.currentValue !== undefined) {
        changed.currentValue = priceData.currentValue;
      }
    }

    const applyDod =
      !!dateOfDeath &&
      (unitsChanged || isZeroOrMissing(source.valueAtDateOfDeath));
    if (applyDod) {
      if (priceData.sharePriceAtDateOfDeath !== undefined) {
        changed.sharePriceAtDateOfDeath = priceData.sharePriceAtDateOfDeath;
      }
      if (priceData.valueAtDateOfDeath !== undefined) {
        changed.valueAtDateOfDeath = priceData.valueAtDateOfDeath;
      }
      /*
        `?? null`, not `undefined` — the update endpoint merges `itemInfo`, and an
        `undefined` key is dropped rather than sent, which would leave a stale
        marker in place.
      */
      changed.dodPriceSourcedAt = priceData.dodPriceSourcedAt ?? null;
    }

    lastPricedUnitsRef.current = numberOfShares;
    appliedRef.current = applyKey;

    if (Object.keys(changed).length === 0) return;
    onCommit(buildPricePatch(changed, itemInfo, scope));
  }, [
    enabled,
    priceData,
    numberOfShares,
    dateOfDeath,
    source,
    itemInfo,
    scope,
    onCommit
  ]);

  if (!enabled) return null;

  return {
    scope,
    figures: shareFiguresFrom(source),
    market: priceData ?? null,
    isFetchingPrice,
    priceNotice,
    automaticPricingUnavailable,
    buildPatch: (changed) => buildPricePatch(changed, itemInfo, scope)
  };
}

/**
 * **A share holding's figures as the RECORD already holds them** — no fetch, no
 * market lookup (VALE-1617).
 *
 * Extracted from {@link useSharePriceSync}'s own return so there is one reading
 * of which `itemInfo` key is the date-of-death value and which is today's. The
 * hook is the live seam and layers a price fetch over this; a surface that only
 * needs to DRAW what is recorded needs exactly this and nothing else.
 *
 * The AI-extract review's detail block is that surface, and it had no seam at
 * all: `sharePriceBlocks` returned `[]` without one, so a share holding's `DOD
 * value` and `Current value` were absent from the review entirely — the two
 * figures a reviewer most needs to agree with, on the one item type that hides
 * them. It has no business fetching a live price mid-review, which is why it
 * gets the recorded figures rather than the hook.
 */
export function shareFiguresFrom(
  source: Record<string, unknown>
): SharePriceFigures {
  return {
    ticker:
      typeof source.ticker === 'string' && source.ticker
        ? source.ticker
        : undefined,
    numberOfShares: toNum(source.numberOfShares),
    lastPriceUpdatedAt:
      typeof source.lastPriceUpdatedAt === 'string'
        ? source.lastPriceUpdatedAt
        : undefined,
    dodPrice: toNum(source.sharePriceAtDateOfDeath),
    dodValue: toNum(source.valueAtDateOfDeath),
    currentPrice: toNum(source.currentPrice),
    currentValue: toNum(source.currentValue),
    overridden: source.currentPriceOverridden === true
  };
}

/**
 * **A read-only seam over the figures a record already holds** (VALE-1617).
 *
 * Everything {@link sharePriceBlocks} needs in order to DRAW its rows, with the
 * live half stated as absent rather than faked: no fetched `market` to restore
 * from, nothing in flight, no notice, and automatic pricing simply not on offer.
 * `buildPatch` is the real one, so a row committed from such a surface writes
 * exactly what the drawer's would.
 *
 * It is a function of the item alone, which is the point: a surface that cannot
 * fetch should not have to assemble a plausible-looking seam by hand, because
 * that assembly is a second definition of what a share's figures are and it
 * drifts. See `compact-inventory-detail.tsx`, which had no seam and silently
 * lost both figure rows.
 */
export function recordedSharePriceSync(
  item: InventoryItem,
  scope: SharePriceScope
): SharePriceSync {
  const itemInfo = (item.itemInfo as Record<string, unknown> | null) ?? {};
  const source = priceSource(itemInfo, scope);
  return {
    scope,
    figures: shareFiguresFrom(source),
    market: null,
    isFetchingPrice: false,
    priceNotice: null,
    automaticPricingUnavailable: false,
    buildPatch: (changed) => buildPricePatch(changed, itemInfo, scope)
  };
}
